import { useCallback, useEffect, useState } from 'react';

import { SpotMediaType } from './types';

export type PendingLocalizationCodes = Record<SpotMediaType, string[]>;

interface UseSpotLocalizationsArgs {
  spotKey: string | null;
}

interface UseSpotLocalizationsResult {
  pendingByType: PendingLocalizationCodes;
  addPendingLanguage: (mediaType: SpotMediaType, code: string) => void;
  removePendingLanguage: (mediaType: SpotMediaType, code: string) => void;
  clearPending: () => void;
}

const emptyPending = (): PendingLocalizationCodes => ({
  image: [],
  video: [],
});

export const useSpotLocalizations = ({
  spotKey,
}: UseSpotLocalizationsArgs): UseSpotLocalizationsResult => {
  const [pendingByType, setPendingByType] =
    useState<PendingLocalizationCodes>(emptyPending);

  useEffect(() => {
    setPendingByType(emptyPending());
  }, [spotKey]);

  const addPendingLanguage = useCallback(
    (mediaType: SpotMediaType, code: string) => {
      setPendingByType((prev) => {
        if (prev[mediaType].includes(code)) {
          return prev;
        }
        return { ...prev, [mediaType]: [...prev[mediaType], code] };
      });
    },
    [],
  );

  const removePendingLanguage = useCallback(
    (mediaType: SpotMediaType, code: string) => {
      setPendingByType((prev) => {
        if (!prev[mediaType].includes(code)) {
          return prev;
        }
        return {
          ...prev,
          [mediaType]: prev[mediaType].filter((c) => c !== code),
        };
      });
    },
    [],
  );

  const clearPending = useCallback(() => {
    setPendingByType(emptyPending());
  }, []);

  return {
    pendingByType,
    addPendingLanguage,
    removePendingLanguage,
    clearPending,
  };
};
